import { useMemo } from 'react';
import type { ComponentType } from 'react';
import { Button, Card } from '@hsc/ui';
import { BackButton } from '../shared/ui/BackButton.js';
import { useMe } from '../shared/api/useMe.js';
import { clearLocalTokens } from '../lib/storage.js';
import { usePlatformMe } from '../shared/api/usePlatformMe.js';
import { buildNavItems, labelForModule } from '../shared/nav/build-nav.js';
import { resolveDashboardKind } from '../shared/nav/resolve-role.js';
import { NoAccessCard } from '../shared/nav/RequireManagementRole.js';
import { AppShell } from '../features/shell/AppShell.js';
import { RoleDashboard } from '../features/dashboards/RoleDashboard.js';
import { CatalogPage } from '../features/catalog/CatalogPage.js';
import { StaffPage } from '../features/staff/StaffPage.js';
import { SchedulesPage } from '../features/schedules/SchedulesPage.js';
import { MyShiftsPage } from '../features/schedules/MyShiftsPage.js';
import { MenuPage } from '../features/menu/MenuPage.js';
import { FnbQueuePage } from '../features/fnb-queue/FnbQueuePage.js';
import { BillingByRole } from '../features/billing/BillingByRole.js';
import { RequestsByRole } from '../features/requests/RequestsByRole.js';
import { RoomsBoard } from '../features/rooms/RoomsBoard.js';
import { RoomsChessboard } from '../features/rooms/RoomsChessboard.js';
import { SettingsPage } from '../features/rooms/SettingsPage.js';
import { StaysPage } from '../features/stays/StaysPage.js';
import { MyStayPage } from '../features/guest-stay/MyStayPage.js';
import { SosPage } from '../features/sos/SosPage.js';
import { HousekeepingPage } from '../features/housekeeping/HousekeepingPage.js';
import { EventsPage } from '../features/events/EventsPage.js';
import { ProfilePage } from '../features/profile/ProfilePage.js';
import { VendorsPage } from '../features/vendors/VendorsPage.js';
import { ReviewsPage } from '../features/reviews/ReviewsPage.js';
import { SlaPage } from '../features/sla/SlaPage.js';
import { InventoryPage } from '../features/inventory/InventoryPage.js';
import { ShellHeaderProvider } from '../features/shell/shell-header.js';

/**
 * Корневой авторизованный экран. Грузит /v1/platform/me (Tanstack Query),
 * строит динамическую навигацию и рендерит дашборд под роль актора.
 *
 * @param activeKey ключ активного модуля (из роута). Если не задан или
 *   совпадает с дашбордом — показываем дашборд роли; иначе ищем реализацию
 *   модуля в MODULE_COMPONENTS, а при её отсутствии — заглушку (фичи
 *   переносятся поэтапно).
 * @param page готовая страница для вложенных роутов каталога
 *   (/services/{categoryCode}, /requests/new — canon design-guest §15/§16):
 *   рендерится внутри AppShell в обход module-резолва, чтобы deep-link
 *   работал независимо от набора modules[] тенанта.
 */
export interface WorkspaceProps {
  activeKey?: string;
  page?: JSX.Element;
  /**
   * Экран сам рендерит «Назад» сверху (guest-access hub/list, меню и пр.) —
   * тогда центральную кнопку Workspace не показываем, чтобы не дублировать.
   */
  selfBack?: boolean;
}

/**
 * Пропсы модульного компонента. `canManage` — управляющая роль (owner/
 * manager): модули с мутациями (номера/проживания) показывают по нему свои
 * действия. Компоненты без мутаций (каталог) проп игнорируют.
 */
export interface ModuleProps {
  canManage: boolean;
}

/**
 * Реализованные модули: ключ из /v1/platform/me modules[] → компонент.
 * Каталог услуг отзывается на все исторические ключи реестра модулей.
 */
const MODULE_COMPONENTS: Readonly<Record<string, ComponentType<ModuleProps>>> = {
  services: CatalogPage,
  'service-catalog': CatalogPage,
  service_catalog: CatalogPage,
  catalog: CatalogPage,
  // Спринт 11 — модуль МЕНЕДЖЕР (персонал / графики).
  staff: StaffPage,
  schedules: SchedulesPage,
  scheduling: SchedulesPage,
  // Личный график сотрудника (staff-portal): СВОИ смены (GET /v1/me/shifts).
  // Отдельный экран, доступен всем staff-ролям + менеджеру; deep-link
  // /my-schedule. НЕ менеджерская сетка (SchedulesPage гейтится owner/manager).
  'my-schedule': MyShiftsPage,
  my_schedule: MyShiftsPage,
  // Заявки — роль-зависимо: менеджер/владелец → лента заявок отеля,
  // гость/персонал → свои заявки (один поддомен, роль-роутинг).
  requests: RequestsByRole,
  // Меню F&B и счёт/фолио гостя (UC-G.09 / UC-G.13).
  menu: MenuPage,
  'room-service': MenuPage,
  // Очередь заказов F&B персоналу (кухня/бар/официант) — блок «Облака».
  'fnb-queue': FnbQueuePage,
  fnb_queue: FnbQueuePage,
  // Счета/фолио — роль-зависимо: гость → «Мои счета», персонал/менеджер →
  // «Все счета» (#338). Один поддомен, роль-роутинг.
  billing: BillingByRole,
  folio: BillingByRole,
  // Менеджер: проживания + номера (спринт 11, флот).
  stays: StaysPage,
  // /rooms — deep-link на шахматку (пункт «Номера» убран; список номеров и
  // типы переехали в «Настройки», правки Артёма).
  rooms: RoomsBoard,
  // «Календарь номеров» (#40): отдельный ключ/path /rooms-calendar →
  // шахматка занятости номеров (RoomsChessboard), НЕ графики смен.
  // Раньше пункт вёл на /scheduling и открывал SchedulesPage.
  'rooms-calendar': RoomsChessboard,
  // «Настройки» (правки Артёма): две вкладки — список номеров (с проваливанием
  // в форму редактирования номера: этаж/№/тип + фото) и типы номеров.
  settings: SettingsPage,
  // Блок 6 «Облака»: экран уборщицы — очередь номеров на уборку/осмотр после
  // выезда. Виден уборщице/менеджеру/владельцу (nav роль staff/owner/manager).
  housekeeping: HousekeepingPage,
  // Анонсы/мероприятия (блок 7 «Облака»): менеджер/владелец — CRUD,
  // гость/персонал — read-only афиша. Роль резолвится по canManage.
  events: EventsPage,
  // Гость: «Моё проживание» (UC-G, design-guest §7) — stay-card, продление,
  // поздний выезд, детали номера (§3). Один поддомен, роль-роутинг: ключ
  // отдельный от менеджерского `stays` (StaysPage — доска заездов).
  'my-stay': MyStayPage,
  my_stay: MyStayPage,
  // SOS (P0, design-guest §15.4) — экстренная заявка персоналу.
  sos: SosPage,
  // Аккаунт: личный кабинет «Профиль» (§2.2).
  profile: ProfilePage,
  // Волна 3 «Облака»: менеджерские экраны поверх готового бэка.
  // Поставщики (internal/vendor_module, GET /v1/vendors).
  vendors: VendorsPage,
  suppliers: VendorsPage,
  // Отзывы (internal/reviews): лента + модерация (скрыть) менеджеру.
  reviews: ReviewsPage,
  // Контроль сроков (SLA): просрочки заявок (GET /v1/admin/requests/sla-breached).
  sla: SlaPage,
  'sla-monitor': SlaPage,
  // Склад (internal/inventory — пока 501): RU-страница-состояние, без фейк-данных.
  inventory: InventoryPage,
};

export function Workspace({ activeKey, page, selfBack }: WorkspaceProps): JSX.Element {
  const { data, isLoading, isError, error } = usePlatformMe();
  // Гостю в шапке/сайдбаре показываем ФАМИЛИЮ (из /v1/me, #486), а не
  // сгенерированный display_name «guest10» (#83). Fallback — прежнее actor.name.
  const me = useMe();

  // Плоские пункты модулей бэкенда — нужны для резолва активного модуля
  // (deep-link по path) и для пересечения с канонической навигацией.
  const navItems = useMemo(() => (data ? buildNavItems(data.modules) : []), [data]);
  const moduleKeys = useMemo(() => navItems.map((n) => n.key), [navItems]);

  if (isLoading) {
    return (
      <main className="ta-content" data-testid="workspace-loading">
        <p className="ta-muted">Загрузка рабочего пространства…</p>
      </main>
    );
  }

  if (isError || !data) {
    const status = (error as { status?: number } | null)?.status;
    // Протухшая/невалидная сессия (401) — не держим пользователя на мёртвой
    // карточке: чистим токены и уводим на /login (silent-refresh уже не помог).
    // ВАЖНО: 403 — это «нет прав на ресурс», НЕ «сессия сдохла». На 403 НЕЛЬЗЯ
    // чистить токены/логаутить/показывать гостя — иначе любой запрет доступа
    // выкидывает пользователя из аккаунта («стал гостем»). 403 обрабатываем
    // ниже как «нет доступа», сессию сохраняем.
    if (status === 401 && typeof window !== 'undefined') {
      clearLocalTokens();
      window.location.assign('/login');
      return (
        <main className="ta-content" data-testid="workspace-error">
          <p className="ta-muted">Сессия истекла — перенаправляем на вход…</p>
        </main>
      );
    }
    // 403 — нет доступа к рабочему пространству: показываем сообщение, токены
    // НЕ трогаем (сессия жива, пользователь остаётся залогинен).
    if (status === 403) {
      return (
        <main className="ta-content" data-testid="workspace-forbidden">
          <Card>
            <h2 className="hsc-card__title">Нет доступа</h2>
            <p>
              У Вашей роли нет прав на этот раздел. Если это ошибка — обратитесь к
              управляющему отеля.
            </p>
            <div className="hsc-stack" style={{ marginTop: '1rem' }}>
              <Button onClick={() => window.location.reload()}>Обновить</Button>
            </div>
          </Card>
        </main>
      );
    }
    const msg = error instanceof Error ? error.message : 'Не удалось загрузить профиль';
    return (
      <main className="ta-content" data-testid="workspace-error">
        <Card>
          <h2 className="hsc-card__title">Ошибка загрузки</h2>
          <p>{msg}</p>
          <div className="hsc-stack" style={{ marginTop: '1rem', flexDirection: 'row', gap: '0.5rem' }}>
            <Button onClick={() => window.location.reload()}>Повторить</Button>
            <Button
              onClick={() => {
                clearLocalTokens();
                window.location.assign('/login');
              }}
            >
              Войти заново
            </Button>
          </div>
        </Card>
      </main>
      );
  }

  const kind = resolveDashboardKind(data.actor.roles);
  const canManage = kind === 'owner' || kind === 'manager';
  // Имя для шапки: гостю — фамилия из /v1/me (#83), иначе actor.name.
  const guestSurname = me.data?.surname?.trim();
  const displayName =
    kind === 'guest' && guestSurname ? guestSurname : data.actor.name;
  const activeModule = activeKey
    ? navItems.find((n) => n.key === activeKey || n.path.endsWith(`/${activeKey}`))
    : undefined;
  // Резолв компонента: сперва по найденному в nav модулю, иначе — напрямую
  // по activeKey. Это позволяет deep-link'ам на реализованные модули
  // (/staff, /schedules, /requests) работать, даже если бэкенд не вернул
  // их в modules[] (фичи опережают наполнение реестра).
  const directComponent = activeKey ? MODULE_COMPONENTS[activeKey] : undefined;
  const ModuleComponent = activeModule ? MODULE_COMPONENTS[activeModule.key] : directComponent;
  const moduleKey = activeModule?.key ?? activeKey;
  // P1-RBAC: менеджерские модули (Персонал/Настройки) — только owner/manager.
  // Покрывает и catch-all-роуты (напр. /settings без вкладки) в обход явных
  // гардов в App.tsx. 403 ≠ logout: токены не трогаем, показываем «нет доступа».
  const MANAGEMENT_ONLY_KEYS = new Set(['staff', 'settings']);
  if (moduleKey && MANAGEMENT_ONLY_KEYS.has(moduleKey) && !canManage) {
    return (
      <ShellHeaderProvider>
        <AppShell
          role={kind}
          moduleKeys={moduleKeys}
          brand={data.tenant.brand}
          actorName={displayName}
        >
          <NoAccessCard />
        </AppShell>
      </ShellHeaderProvider>
    );
  }

  // Показываем ли модуль/страницу (не голый дашборд) — тогда сверху нужна
  // брендовая «‹ Назад» на дашборд (#82, патент «любой экран = Назад сверху»).
  // selfBack — экран сам рендерит «Назад» (не дублируем).
  const isDashboard = !page && !activeModule && !ModuleComponent;
  const showBack = !isDashboard && !selfBack;

  return (
    <ShellHeaderProvider>
    <AppShell
      role={kind}
      moduleKeys={moduleKeys}
      brand={data.tenant.brand}
      actorName={displayName}
    >
      {showBack ? <BackButton to="/" testId="workspace-back" /> : null}
      {page ??
        (activeModule || ModuleComponent ? (
          <section data-testid={`module-${moduleKey}`}>
            {ModuleComponent ? (
              <ModuleComponent canManage={canManage} />
            ) : (
              <>
                <h1 className="hsc-page__title">{labelForModule(activeModule!.key)}</h1>
                <Card>
                  <p>
                    Модуль «{activeModule!.label}» в разработке. Доступные действия:{' '}
                    {activeModule!.services.length > 0 ? activeModule!.services.join(', ') : '—'}.
                  </p>
                </Card>
              </>
            )}
          </section>
        ) : (
          <RoleDashboard kind={kind} brand={data.tenant.brand} actorName={displayName} />
        ))}
    </AppShell>
    </ShellHeaderProvider>
  );
}
